import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Switch, Checkbox, Box, Typography, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReusableGrid from "../../../components/ag grid/ReusableGrid";
import { BRAND } from "../../../components/auth/authTheme";

const PAGE_SIZE = 10;
const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 44;
const PAGINATION_HEIGHT = 52;
const GRID_HEIGHT =
  ROW_HEIGHT * PAGE_SIZE + HEADER_HEIGHT + PAGINATION_HEIGHT;

const DISPLAY_ACTIONS = [
  "view",
  "create",
  "edit",
  "delete",
  "approve",
  "export",
  "import",
];

const PermissionsTab = ({ modulesData, permissions, setPermissions, basePermissions = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const gridRef = useRef();
  const [allActions, setAllActions] = useState([]);

  useEffect(() => {
    if (!modulesData) return;

    const keys = new Set();
    modulesData.forEach((mod) => {
      mod.actions.forEach((act) => keys.add(act.key));
    });
    setAllActions([...keys]);
  }, [modulesData]);

  const visibleActions = useMemo(() => {
    const fromDisplay = DISPLAY_ACTIONS.filter((action) =>
      allActions.includes(action)
    );
    if (fromDisplay.length > 0) return fromDisplay;

    return allActions.filter(
      (action) =>
        !["archive", "restore", "assign", "unassign", "reject"].includes(
          action
        )
    );
  }, [allActions]);

  const allActionsRef = useRef(allActions);
  allActionsRef.current = allActions;

  const isBasePermission = useCallback((module, action) => {
    if (!basePermissions || basePermissions.length === 0) return false;
    const baseMod = basePermissions.find(b => b.module === module);
    if (!baseMod) return false;
    if (action === "enabled") return baseMod.enabled;
    return Boolean(baseMod[action]);
  }, [basePermissions]);

  const handleToggle = useCallback((module, field, value) => {
    setPermissions((prev) => {
      const updatedData = prev.map((row) => {
        if (row.module !== module) return row;

        if (field === "enabled") {
          const updated = { ...row, enabled: value };
          allActionsRef.current.forEach((action) => {
            updated[action] = value ? action === "view" : false;
          });
          return updated;
        }

        return { ...row, [field]: value };
      });

      if (!isMobile) {
        setTimeout(() => {
          const api = gridRef.current;
          if (api?.ensureIndexVisible) {
            const rowIndex = updatedData.findIndex((r) => r.module === module);
            if (rowIndex >= 0) api.ensureIndexVisible(rowIndex, "middle");
          }
        }, 0);
      }

      return updatedData;
    });
  }, [setPermissions, isMobile]);

  const actionRenderer = useCallback(
    (field) => (params) => {
      const isBase = isBasePermission(params.data.module, field);
      return (
        <Checkbox
          checked={Boolean(params.data[field]) || isBase}
          onChange={(e) =>
            handleToggle(params.data.module, field, e.target.checked)
          }
          disabled={(!params.data.enabled && !isBasePermission(params.data.module, 'enabled')) || isBase}
          size="small"
          sx={{
            color: BRAND.border,
            "&.Mui-checked": { color: BRAND.orange },
            "&.Mui-disabled": { opacity: 0.35 },
          }}
        />
      );
    },
    [handleToggle, isBasePermission]
  );

  const columnDefs = useMemo(() => {
    const moduleColumn = {
      headerName: "Module",
      field: "moduleLabel",
      pinned: "left",
      cellClass: "module-permission-cell wrap-text",
      minWidth: 220,
      flex: 1.5,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: (params) => {
        const isBaseEnabled = isBasePermission(params.data.module, "enabled");
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
            <Switch
              size="small"
              checked={Boolean(params.data.enabled) || isBaseEnabled}
              onChange={(e) =>
                handleToggle(params.data.module, "enabled", e.target.checked)
              }
              disabled={isBaseEnabled}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: BRAND.orange,
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: BRAND.orange,
                },
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500, color: BRAND.text }}>
              {params.data.moduleLabel}
            </Typography>
          </Box>
        );
      },
    };

    const actionColumns = visibleActions.map((action) => ({
      headerName: action
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      field: action,
      width: 88,
      minWidth: 76,
      maxWidth: 100,
      suppressSizeToFit: true,
      sortable: false,
      headerClass: "permission-action-header",
      cellClass: "permission-action-cell",
      cellRenderer: actionRenderer(action),
    }));

    return [moduleColumn, ...actionColumns];
  }, [visibleActions, handleToggle, actionRenderer]);

  const gridDefaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: false,
      minWidth: 76,
    }),
    []
  );

  return (
    <Box>
      <Typography variant="body2" sx={{ color: BRAND.muted, mb: 1.5 }}>
        Toggle a module to enable permissions, then select allowed actions.
        {!isMobile && ` Showing ${PAGE_SIZE} modules per page.`}
      </Typography>

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 4 }}>
          {permissions.map((perm) => (
            <Accordion 
              key={perm.module} 
              disableGutters 
              sx={{
                 border: `1px solid ${BRAND.border}`,
                 borderRadius: '12px !important',
                 boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                 '&:before': { display: 'none' },
                 overflow: 'hidden',
                 bgcolor: '#fff'
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: '#fff', 
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    m: 0,
                    py: 1
                  } 
                }}
              >
                <Typography sx={{ fontWeight: 600, color: BRAND.navy, fontSize: '0.95rem' }}>
                  {perm.moduleLabel}
                </Typography>
                <Switch 
                  size="small"
                  checked={Boolean(perm.enabled) || isBasePermission(perm.module, "enabled")}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleToggle(perm.module, "enabled", e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isBasePermission(perm.module, "enabled")}
                  sx={{
                    mr: 2,
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: BRAND.orange,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: BRAND.orange,
                    },
                  }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#fafafa', p: 0 }}>
                {visibleActions.map((action) => (
                   <Box 
                     key={action} 
                     sx={{ 
                       display: 'flex', 
                       alignItems: 'center', 
                       justifyContent: 'space-between', 
                       px: 2, 
                       py: 1.5, 
                       borderTop: `1px solid ${BRAND.border}` 
                     }}
                   >
                      <Typography sx={{ fontSize: '0.85rem', color: BRAND.navy, fontWeight: 500, textTransform: 'capitalize' }}>
                        {action.replace(/_/g, " ")}
                      </Typography>
                      <Checkbox 
                        checked={Boolean(perm[action]) || isBasePermission(perm.module, action)}
                        onChange={(e) => handleToggle(perm.module, action, e.target.checked)}
                        disabled={(!perm.enabled && !isBasePermission(perm.module, 'enabled')) || isBasePermission(perm.module, action)}
                        size="medium"
                        sx={{
                          color: BRAND.border,
                          p: 0.5,
                          "&.Mui-checked": { color: BRAND.orange },
                          "&.Mui-disabled": { opacity: 0.35 },
                        }}
                      />
                   </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Box className="permission-grid-wrapper list-grid-container">
          <ReusableGrid
            rowData={permissions}
            columnDefs={columnDefs}
            pagination={true}
            gridRef={gridRef}
            domLayout="normal"
            height={`${GRID_HEIGHT}px`}
            pageSize={PAGE_SIZE}
            defaultColDef={gridDefaultColDef}
            emptyMessage="No permission modules available"
          />
        </Box>
      )}
    </Box>
  );
};

export default PermissionsTab;
